import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'utils/dayjs';
import { Page } from 'utils/page-utils';
import SegmentUtils, { getOS } from 'utils/segment';
import ErrorUtils from 'utils/error-utils';
import SearchTracking from 'utils/search-tracking';
import StorageUtils from '@spothero/utils/storage';
import getParkingType from '../utils/parking-type';
import getUserLocation from '../utils/user-location';
import { calculateTimeDifference, getQueryValue, createTopSearchResultsList, } from '../utils/top-search-results';
import transformFilters from '../utils/filters';
import { getStartAndEndDateOfPowerBookingPeriod } from 'utils/power-booking/utils';
import { getRouteParam, paramNameOptions } from 'utils/url-utils';
import { getSearchPageViewString } from 'utils/search-page-view';
import { isOversizedFeatureEnabled, isSpotOversize } from 'utils/search-vehicle';
import { getTotalPriceUserPreference, TotalPriceSessionPreference, } from 'utils/total-price-session-utils';
import { getExperimentsFromCookie } from 'utils/experiment';
import { StorageKeys, StorageTypes } from 'common/constants/storageKeys';
import { getGooglePlacesTypes } from 'utils/google-places-type-utils';
export default function trackProductsSearched(args) {
    try {
        const view = getSearchPageViewString(getRouteParam(paramNameOptions.VIEW));
        const frontendExperiments = getExperimentsFromCookie();
        const { city: { display_name: cityName, slug: citySlug, state, timezone }, destination: { id: destinationId, title: destinationName, google_places: destinationGooglePlaces, airport, }, event: { id: eventId, title: eventName }, eventPackage: { title: eventPackageName }, destinationTitle, searchRequest: { monthly, referrer, search_string: searchQuery, latitude, longitude, starts, ends, pageType, powerBooking: isPowerBooking, powerBookingPeriods, powerBookingSource = null, }, filters, activeAmenities, spots = [], searchAPIVersion, sortBy, breadcrumbs, vehicle, backendExperiments, } = args;
        const destinationCategory = breadcrumbs?.categoryBreadcrumb?.label;
        const airportName = airport ? airport.airport_name : null;
        const isEvent = Boolean(eventId);
        const isEventPackage = pageType === Page.EVENT_PACKAGES_SEARCH;
        const { sessionUUID, searchUUID, action: trackingAction, actionUUID, } = SearchTracking.getValues();
        const parkingType = getParkingType({
            isMonthly: monthly,
            isAirport: airport,
            isEvent,
            isEventPackage,
            isPowerBooking,
        });
        const searchSource = !isEmpty(referrer) ? referrer : 'map';
        const googlePlaceId = get(destinationGooglePlaces, '0.google_places_place_id');
        const googlePlaceName = get(destinationGooglePlaces, '0.google_places_name');
        const query = getQueryValue({
            pageType,
            cityName,
            destinationName,
            eventName,
            eventPackageName,
            airportName,
            searchQuery,
        });
        let startDate = starts;
        let endDate = ends;
        if (isPowerBooking) {
            const { startingDate } = getStartAndEndDateOfPowerBookingPeriod(powerBookingPeriods);
            const startingPeriodOfPowerBooking = powerBookingPeriods.find(({ starts: periodStarts }) => dayjs(periodStarts).isSame(dayjs(startingDate)));
            startDate = startingDate;
            endDate = startingPeriodOfPowerBooking?.ends;
        }
        const showTotal = getTotalPriceUserPreference() === TotalPriceSessionPreference.On;
        const includeOversize = isOversizedFeatureEnabled(parkingType);
        const isVehicleSelected = Boolean(vehicle?.id) && includeOversize;
        const vehicleMake = (isVehicleSelected && vehicle?.make) || null;
        const vehicleModel = (isVehicleSelected && vehicle?.model) || null;
        const oversizeCount = isVehicleSelected
            ? spots.filter(s => isSpotOversize(s)).length
            : 0;
        const vehicleProperties = includeOversize
            ? {
                vehicle_in_search_viewed: true,
                vehicle_make: vehicleMake,
                vehicle_model: vehicleModel,
                oversize_count: oversizeCount,
            }
            : {};
        const properties = {
            device_os: getOS(),
            page_type: pageType,
            action_id: actionUUID,
            action_type: trackingAction,
            ...(airport && {
                airport_code: airport.iata_code,
            }),
            city: citySlug,
            destination_id: destinationId,
            destination_name: destinationName,
            event_id: eventId,
            event_name: eventName,
            filters: transformFilters(filters),
            'filters selected': activeAmenities.join(),
            google_places_name: googlePlaceName,
            google_places_place_id: googlePlaceId,
            map_center: {
                latitude,
                longitude,
            },
            'number of filters applied': activeAmenities.length,
            parking_type: parkingType,
            query,
            search_api_version: searchAPIVersion,
            search_end_time_utc: monthly || isEventPackage ? null : dayjs(endDate).toISOString(),
            search_id: searchUUID,
            search_lead_time: isEventPackage
                ? null
                : calculateTimeDifference({
                    // Setting timezones here allows for normalization of time differences across timezones
                    starts: dayjs().tz(timezone),
                    ends: dayjs(startDate).tz(timezone, true),
                    unit: 'minute',
                }),
            search_length: monthly || isEventPackage
                ? null
                : calculateTimeDifference({
                    starts: startDate,
                    ends: endDate,
                }),
            search_source: searchSource,
            search_start_time_utc: isEventPackage
                ? null
                : dayjs(startDate).toISOString(),
            session_id: sessionUUID,
            sort_by: sortBy,
            state,
            top_search_results_list: createTopSearchResultsList({
                spots,
                airport,
                sortBy,
                parkingType,
                isMonthly: parkingType === 'monthly',
                filters,
                showDiscountRateFields: true,
            }),
            total_price_toggle_on: showTotal,
            power_booking: isPowerBooking,
            source: powerBookingSource,
            days_selected: powerBookingPeriods.map(({ starts: periodStarts, ends: periodEnds }) => ({
                search_start_time_utc: dayjs(periodStarts).toISOString(),
                search_end_time_utc: dayjs(periodEnds).toISOString(),
            })),
            view,
            partner_affiliate: StorageUtils.get(StorageKeys.PARTNER_AFFILIATE, StorageTypes.SESSION) || null,
            ...getUserLocation(),
            /* --- DEPRECATED PROPERTIES --- */
            ...(airport && {
                'airport code search': airport.iata_code, // MARKED FOR DEPRECATION: airport_code will be used moving forward
            }),
            ...(destinationCategory && {
                destination_category: destinationCategory,
            }),
            ...vehicleProperties,
            ...(backendExperiments && {
                backend_experiments: backendExperiments,
            }),
            ...(frontendExperiments && {
                frontend_experiments: frontendExperiments,
            }),
            'parking type': parkingType, // MARKED FOR DEPRECATION: parking_type will be used moving forward
            'search city': cityName, // MARKED FOR DEPRECATION: city will be used moving forward
            'search end time': monthly || isEventPackage ? null : dayjs(endDate).toISOString(), // MARKED FOR DEPRECATION: search_end_time_utc will be used moving forward
            'search id': searchUUID, // MARKED FOR DEPRECATION: search_id will be used moving forward
            'search query': searchQuery || destinationTitle, // MARKED FOR DEPRECATION: query will be used moving forward
            'search source': searchSource, // MARKED FOR DEPRECATION: search_source will be used moving forward
            'search start time': isEventPackage
                ? null
                : dayjs(startDate).toISOString(), // MARKED FOR DEPRECATION: search_start_time_utc will be used moving forward
            'search type': trackingAction, // MARKED FOR DEPRECATION: action_type will be used moving forward
            'session id': sessionUUID, // MARKED FOR DEPRECATION: session_id will be used moving forward
            ...getGooglePlacesTypes(parkingType),
        };
        SegmentUtils.track({
            event: 'Products Searched',
            properties,
            additionalOptions: {
                context: {
                    protocols: {
                        event_version: 25,
                    },
                },
            },
        });
    }
    catch (error) {
        ErrorUtils.sendSentryMessage({
            error,
            customErrorMessage: 'Segment Event Failed - trackProductsSearched',
        });
    }
}
